import { FUNDERS, type Funder, isFunder } from './funder';

export type FundingTransitionOperation = 'ADD' | 'END' | 'REPLACE';

export type FundingTransitionFinanceMethod =
  | 'ACCEPTED_AS_HISTORICAL'
  | 'REBILLED_EXTERNALLY'
  | 'CORRECTED_IN_PLATFORM'
  | 'NO_POSTED_MATCH';

export type FundingTransitionApprovalState =
  | 'NOT_REQUIRED'
  | 'PENDING'
  | 'APPROVED'
  | 'BREAK_GLASS'
  | 'REJECTED'
  | 'STALE';

export type FundingTransitionBlockerCode =
  | 'SAH_LIFECYCLE_REVIEW'
  | 'SAH_PATHWAY_CLOSEOUT_REQUIRED'
  | 'CARE_PLAN_ALLOCATION_REVIEW'
  | 'DELIVERY_FINANCE_RECONCILIATION'
  | 'INDEPENDENT_APPROVAL_REQUIRED'
  | 'SUCCESSOR_CARE_PLAN_REQUIRED'
  | 'TARGET_EVIDENCE_REQUIRED'
  | 'TARGET_FUNDING_ACCOUNT_REQUIRED'
  | 'TARGET_AUTHORISATION_REQUIRED'
  | 'TARGET_RESIDENTIAL_ADMISSION_REQUIRED'
  | 'TARGET_AN_ACC_CLASSIFICATION_REQUIRED';

export const FUNDING_TRANSITION_FINANCE_METHOD_LABELS: Readonly<
  Record<FundingTransitionFinanceMethod, string>
> = {
  ACCEPTED_AS_HISTORICAL: 'Accept as historical billing',
  REBILLED_EXTERNALLY: 'Rebilled outside Statura',
  CORRECTED_IN_PLATFORM: 'Corrected in Statura',
  NO_POSTED_MATCH: 'No matching posted services',
};

export const FUNDING_TRANSITION_BLOCKER_LABELS: Readonly<
  Record<FundingTransitionBlockerCode, string>
> = {
  SAH_LIFECYCLE_REVIEW: 'Complete the Support at Home plan lifecycle',
  SAH_PATHWAY_CLOSEOUT_REQUIRED: 'Close the active pathway funding',
  CARE_PLAN_ALLOCATION_REVIEW: 'Reallocate affected care-plan services',
  DELIVERY_FINANCE_RECONCILIATION: 'Resolve posted-service finance evidence',
  INDEPENDENT_APPROVAL_REQUIRED: 'Obtain independent approval',
  SUCCESSOR_CARE_PLAN_REQUIRED: 'Link the successor care plan and budget',
  TARGET_EVIDENCE_REQUIRED: 'Add payer decision or agreement evidence',
  TARGET_FUNDING_ACCOUNT_REQUIRED: 'Create the payer billing account',
  TARGET_AUTHORISATION_REQUIRED: 'Record an active funding authorisation',
  TARGET_RESIDENTIAL_ADMISSION_REQUIRED: 'Complete residential admission readiness',
  TARGET_AN_ACC_CLASSIFICATION_REQUIRED: 'Record the AN-ACC classification',
};

export function isFundingTransitionBlockerCode(
  value: unknown,
): value is FundingTransitionBlockerCode {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(FUNDING_TRANSITION_BLOCKER_LABELS, value)
  );
}

export function wholeLineFundingAllocation(
  program: Funder,
): Partial<Record<Funder, { pct: number }>> {
  return { [program]: { pct: 1 } };
}

export type FundingTransitionInput = {
  currentPrograms: readonly string[] | null | undefined;
  operation: FundingTransitionOperation;
  fromProgram?: Funder | null;
  toProgram?: Funder | null;
  /**
   * Set only from {@link racPlannedRegistrationPromotionEligible}. Lets ADD
   * re-select AN_ACC that registration already wrote to the profile, so the
   * governed apply can promote the PLANNED registration enrolment to ACTIVE.
   */
  racPlannedRegistrationPromotion?: boolean;
};

export type FundingTransitionResult =
  | {
      ok: true;
      beforePrograms: Funder[];
      afterPrograms: Funder[];
      retainedPrograms: Funder[];
      addedPrograms: Funder[];
      endedPrograms: Funder[];
      promotesPlannedRegistration?: true;
    }
  | {
      ok: false;
      code:
        | 'CURRENT_PROGRAM_INVALID'
        | 'SOURCE_REQUIRED'
        | 'TARGET_REQUIRED'
        | 'SOURCE_NOT_ACTIVE'
        | 'TARGET_ALREADY_ACTIVE'
        | 'SOURCE_EQUALS_TARGET'
        | 'LAST_PROGRAM_CANNOT_END';
      message: string;
    };

function canonicalPrograms(values: readonly string[] | null | undefined): Funder[] | null {
  if (!Array.isArray(values)) return [];
  const result: Funder[] = [];
  for (const value of values) {
    if (!isFunder(value)) return null;
    if (!result.includes(value)) result.push(value);
  }
  return result;
}

/**
 * Pure transition contract shared by UI/tests and mirrored by the controlled
 * database RPC. It deliberately changes only the selected stream:
 *
 * - ADD appends one payer and retains every current payer.
 * - END removes one payer but never permits an empty funding profile.
 * - REPLACE substitutes one selected payer in place and retains all co-payers.
 *
 * Care-plan service allocations are outside this function by design. They must
 * be reviewed explicitly before an END/REPLACE transition can be applied.
 */
export function deriveFundingTransition(input: FundingTransitionInput): FundingTransitionResult {
  const beforePrograms = canonicalPrograms(input.currentPrograms);
  if (beforePrograms == null) {
    return {
      ok: false,
      code: 'CURRENT_PROGRAM_INVALID',
      message: 'The current funding profile contains a non-canonical program.',
    };
  }

  const fromProgram = input.fromProgram ?? null;
  const toProgram = input.toProgram ?? null;

  if ((input.operation === 'END' || input.operation === 'REPLACE') && !fromProgram) {
    return {
      ok: false,
      code: 'SOURCE_REQUIRED',
      message: 'Select the funding program being ended or replaced.',
    };
  }
  if ((input.operation === 'ADD' || input.operation === 'REPLACE') && !toProgram) {
    return {
      ok: false,
      code: 'TARGET_REQUIRED',
      message: 'Select the funding program being added.',
    };
  }
  if (fromProgram && !beforePrograms.includes(fromProgram)) {
    return {
      ok: false,
      code: 'SOURCE_NOT_ACTIVE',
      message: 'The selected source program is not active for this client.',
    };
  }
  if (
    input.operation === 'ADD' &&
    toProgram === RAC_PLANNED_REGISTRATION_PROMOTION_PROGRAM &&
    beforePrograms.includes(toProgram) &&
    input.racPlannedRegistrationPromotion === true
  ) {
    // Mirrors record_client_funding_transition_controlled: the profile is
    // unchanged and the apply converts the PLANNED registration enrolment.
    return {
      ok: true,
      beforePrograms,
      afterPrograms: [...beforePrograms],
      retainedPrograms: [...beforePrograms],
      addedPrograms: [],
      endedPrograms: [],
      promotesPlannedRegistration: true,
    };
  }
  if (input.operation === 'ADD' && toProgram && beforePrograms.includes(toProgram)) {
    return {
      ok: false,
      code: 'TARGET_ALREADY_ACTIVE',
      message: 'The selected funding program is already active.',
    };
  }
  if (input.operation === 'REPLACE' && fromProgram === toProgram) {
    return {
      ok: false,
      code: 'SOURCE_EQUALS_TARGET',
      message: 'The replacement program must be different from the current program.',
    };
  }
  if (input.operation === 'END' && beforePrograms.length === 1) {
    return {
      ok: false,
      code: 'LAST_PROGRAM_CANNOT_END',
      message: 'Add or replace funding before ending the client’s final active program.',
    };
  }

  let afterPrograms: Funder[];
  if (input.operation === 'ADD') {
    afterPrograms = [...beforePrograms, toProgram as Funder];
  } else if (input.operation === 'END') {
    afterPrograms = beforePrograms.filter((program) => program !== fromProgram);
  } else {
    afterPrograms = beforePrograms.map((program) =>
      program === fromProgram ? (toProgram as Funder) : program,
    );
    afterPrograms = afterPrograms.filter(
      (program, index) => afterPrograms.indexOf(program) === index,
    );
  }

  return {
    ok: true,
    beforePrograms,
    afterPrograms,
    retainedPrograms: beforePrograms.filter((program) => afterPrograms.includes(program)),
    addedPrograms: afterPrograms.filter((program) => !beforePrograms.includes(program)),
    endedPrograms: beforePrograms.filter((program) => !afterPrograms.includes(program)),
  };
}

export function availableFundingTransitionTargets(
  currentPrograms: readonly string[] | null | undefined,
  options: { racPlannedRegistrationPromotion?: boolean } = {},
): Funder[] {
  const canonical = canonicalPrograms(currentPrograms) ?? [];
  return FUNDERS.filter(
    (program) =>
      !canonical.includes(program) ||
      (program === RAC_PLANNED_REGISTRATION_PROMOTION_PROGRAM &&
        options.racPlannedRegistrationPromotion === true),
  );
}

/** Only permanent residential registration is promoted through the funding card. */
export const RAC_PLANNED_REGISTRATION_PROMOTION_PROGRAM: Funder = 'AN_ACC';

export type RacPlannedRegistrationEnrollment = {
  status: string | null;
  program_code: string | null;
  source_table: string | null;
  source_id: string | null;
  idempotency_key: string | null;
  start_date: string | null;
  end_date: string | null;
};

const TERMINAL_CLIENT_STATUSES = new Set([
  'DECEASED',
  'DELETED',
  'ANONYMISED',
  'ANONYMIZED',
  'ARCHIVED',
  'DISCHARGED',
]);

function currentOn(row: RacPlannedRegistrationEnrollment, today: string): boolean {
  return (
    typeof row.start_date === 'string' &&
    row.start_date <= today &&
    (row.end_date == null || row.end_date >= today)
  );
}

/**
 * Client-side mirror of the ADD-already-present exception in
 * record_client_funding_transition_controlled and
 * internal.rac_planned_client_setup_allowed. Every condition must hold; the
 * database re-checks all of them, so this only decides whether to offer AN_ACC.
 */
export function racPlannedRegistrationPromotionEligible(input: {
  clientId: string;
  today: string;
  clientStatus: string | null;
  clientDischargeDate: string | null;
  currentPrograms: readonly string[] | null | undefined;
  enrollments: readonly RacPlannedRegistrationEnrollment[];
  tenantResidentialLive: boolean;
  tenantSetupAllowed: boolean;
}): boolean {
  if (!input.clientId || !input.today) return false;
  if (!input.tenantResidentialLive || !input.tenantSetupAllowed) return false;
  if (input.clientDischargeDate) return false;
  if (TERMINAL_CLIENT_STATUSES.has(String(input.clientStatus ?? '').toUpperCase())) return false;
  const programs = canonicalPrograms(input.currentPrograms) ?? [];
  if (!programs.includes(RAC_PLANNED_REGISTRATION_PROMOTION_PROGRAM)) return false;
  if (
    input.enrollments.some(
      (row) => row.status === 'ACTIVE' && String(row.program_code ?? '').startsWith('RAC_'),
    )
  ) {
    return false;
  }
  return input.enrollments.some(
    (row) =>
      row.status === 'PLANNED' &&
      row.program_code === 'RAC_PERMANENT' &&
      row.source_table === 'clients' &&
      row.source_id === input.clientId &&
      row.idempotency_key === `rac-registration-planned:${input.clientId}` &&
      currentOn(row, input.today),
  );
}
